// Cache bust: 2026-02-04T15:26
import { StrictMode } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { HelmetProvider } from 'react-helmet-async'
import './index.css'
import App from './App.tsx'
import { queryClient } from './lib/queryClient'
import { GoogleOAuthProvider } from './providers/GoogleOAuthProvider'
import { initPostHog } from './lib/analytics'
import { initializeTheme } from './lib/theme'
import { initSentry } from './lib/sentry'

// Initialize Sentry error tracking (skipped in dev, requires VITE_SENTRY_DSN)
initSentry()

// Initialize theme from localStorage (defaults to dark, prevents FOUC)
initializeTheme()

// Initialize PostHog product analytics (cookieless mode - no consent banner needed)
initPostHog()

const container = document.querySelector('#root')!

const tree = (
  <StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <GoogleOAuthProvider>
          <App />
        </GoogleOAuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </StrictMode>
)

/**
 * Hydrate the prerendered routes; client-render everything else.
 *
 * `scripts/prerender.mjs` writes real markup into `#root` for `/`, `/pricing`,
 * `/terms`, `/privacy` and `/auth`. `createRoot().render()` would THROW THAT
 * AWAY — it discards the container's existing children and renders from
 * scratch — so the visitor would see the real content paint, get wiped, show
 * the `PageLoader` spinner while the route's `lazy()` chunk downloads, and then
 * see the content again. Prerendering would have made the first paint *worse*
 * while looking like an improvement in every byte-level measurement.
 *
 * The branch is required, not defensive: `app.html` — the SPA fallback nginx
 * serves for /chat, /settings and every unmatched URL — ships a deliberately
 * EMPTY `#root`, and `hydrateRoot` on an empty container is itself a mismatch.
 * Presence of a child is exactly the "was this route prerendered?" signal.
 */
if (container.firstChild) {
  hydrateRoot(container, tree)
} else {
  createRoot(container).render(tree)
}
